import React, { useState, useCallback } from 'react';
import { Row, Col, Card, Tab, Nav, Button, Badge, Table, Modal } from 'react-bootstrap';
import apiService from '../../services/api';
import InfrastructureSectionPanel from './InfrastructureSectionPanel';

// ── Replication Status ────────────────────────────────────────────────────────

const formatLag = (lag) => {
    if (lag == null) return '—';
    if (typeof lag === 'object' && lag.milliseconds != null) {
        const ms = Number(lag.milliseconds);
        return ms < 1 ? '< 1 ms' : `${ms.toFixed(2)} ms`;
    }
    return String(lag);
};

const ReplicationStatusPanel = ({ data }) => {
    if (!data || data.length === 0) {
        return (
            <div className="text-center py-4 text-muted small">
                <i className="bi bi-info-circle me-1"></i>No replication standbys connected.
            </div>
        );
    }

    return (
        <div className="table-responsive">
            <Table size="sm" bordered hover className="small mb-0">
                <thead className="table-light">
                    <tr>
                        <th>Application</th>
                        <th>Client</th>
                        <th>State</th>
                        <th>Sync State</th>
                        <th>Lag (bytes)</th>
                        <th>Write Lag</th>
                        <th>Flush Lag</th>
                        <th>Replay Lag</th>
                        <th>Priority</th>
                        <th>Reply Time</th>
                    </tr>
                </thead>
                <tbody>
                    {data.map((row, i) => (
                        <tr key={i}>
                            <td className="fw-semibold">{row.application_name || '—'}</td>
                            <td>{row.client_addr || row.client_hostname || '—'}</td>
                            <td>
                                <Badge bg={row.state === 'streaming' ? 'success' : 'warning'} className="text-uppercase">
                                    {row.state || '—'}
                                </Badge>
                            </td>
                            <td>
                                <Badge bg={row.sync_state === 'sync' ? 'primary' : 'secondary'} className="text-uppercase">
                                    {row.sync_state || '—'}
                                </Badge>
                            </td>
                            <td>{row.replication_lag_bytes != null ? Number(row.replication_lag_bytes).toLocaleString() : '—'}</td>
                            <td>{formatLag(row.write_lag)}</td>
                            <td>{formatLag(row.flush_lag)}</td>
                            <td>{formatLag(row.replay_lag)}</td>
                            <td>{row.sync_priority ?? '—'}</td>
                            <td className="text-nowrap">{row.reply_time ? new Date(row.reply_time).toLocaleString() : '—'}</td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </div>
    );
};

// ── Vacuum Required ───────────────────────────────────────────────────────────

const urgencyVariant = (urgency) => {
    if (urgency === 'HIGH') return 'danger';
    if (urgency === 'MEDIUM') return 'warning';
    return 'success';
};

const VacuumRequiredPanel = ({ data }) => {
    if (!data || data.length === 0) {
        return (
            <div className="text-center py-4 text-muted small">
                <i className="bi bi-check-circle me-1 text-success"></i>No tables require immediate attention.
            </div>
        );
    }

    return (
        <div className="table-responsive">
            <Table size="sm" bordered hover className="small mb-0">
                <thead className="table-light">
                    <tr>
                        <th>Schema</th>
                        <th>Table</th>
                        <th>Size</th>
                        <th>Dead Tuples</th>
                        <th>Dead %</th>
                        <th>Urgency</th>
                        <th>Last Vacuum</th>
                        <th>Hrs Since Vacuum</th>
                        <th>Last Analyze</th>
                        <th>Hrs Since Analyze</th>
                        <th>Analyze Note</th>
                    </tr>
                </thead>
                <tbody>
                    {data.map((row, i) => (
                        <tr key={i}>
                            <td>{row.schemaname || '—'}</td>
                            <td className="fw-semibold">{row.tablename || '—'}</td>
                            <td className="text-nowrap">{row.total_size || '—'}</td>
                            <td>{row.n_dead_tup != null ? Number(row.n_dead_tup).toLocaleString() : '—'}</td>
                            <td>{row.dead_pct != null ? `${row.dead_pct}%` : '—'}</td>
                            <td>
                                <Badge bg={urgencyVariant(row.vacuum_urgency)}>
                                    {row.vacuum_urgency || '—'}
                                </Badge>
                            </td>
                            <td className="text-nowrap">{row.last_autovacuum || row.last_manual_vacuum || '—'}</td>
                            <td>{row.hours_since_last_vacuum != null ? `${row.hours_since_last_vacuum}h` : '—'}</td>
                            <td className="text-nowrap">{row.last_autoanalyze || row.last_manual_analyze || '—'}</td>
                            <td>{row.hours_since_last_analyze != null ? `${row.hours_since_last_analyze}h` : '—'}</td>
                            <td className="text-muted fst-italic">{row.analyze_note || '—'}</td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </div>
    );
};

// ── Slow Queries ──────────────────────────────────────────────────────────────

const SlowQueryTable = ({ rows, emptyMessage }) => {
    const [sortCol, setSortCol] = useState(null);
    const [sortDir, setSortDir] = useState('asc');
    const [modalQuery, setModalQuery] = useState(null);
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        if (modalQuery) {
            navigator.clipboard.writeText(modalQuery).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            });
        }
    };

    if (!rows || rows.length === 0) {
        return (
            <div className="text-center py-3 text-muted small">
                <i className="bi bi-check-circle me-1 text-success"></i>{emptyMessage}
            </div>
        );
    }

    const handleSort = (col) => {
        if (sortCol === col) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortCol(col);
            setSortDir('asc');
        }
    };

    const sortFieldMap = {
        calls: 'calls',
        total: 'total_time_s',
        rows: 'rows',
        min: 'min_time_s',
        max: 'max_time_s',
    };

    const sortedRows = sortCol
        ? [...rows].sort((a, b) => {
            const field = sortFieldMap[sortCol];
            const aVal = parseFloat(a[field]) ?? 0;
            const bVal = parseFloat(b[field]) ?? 0;
            return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
        })
        : rows;

    const SortIcon = ({ col }) => {
        if (sortCol !== col) return <i className="bi bi-arrow-down-up text-muted ms-1" style={{ fontSize: '0.65rem' }}></i>;
        return <i className={`bi bi-arrow-${sortDir === 'asc' ? 'up' : 'down'} ms-1`} style={{ fontSize: '0.65rem' }}></i>;
    };

    const sortableTh = { cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' };

    return (
        <>
            <div className="table-responsive">
                <Table size="sm" bordered hover className="small mb-0">
                    <thead className="table-light">
                        <tr>
                            <th style={{ minWidth: 300 }}>Query</th>
                            <th style={sortableTh} onClick={() => handleSort('calls')}>Calls <SortIcon col="calls" /></th>
                            <th>Avg (s)</th>
                            <th style={sortableTh} onClick={() => handleSort('total')}>Total (s) <SortIcon col="total" /></th>
                            <th style={sortableTh} onClick={() => handleSort('min')}>Min (s) <SortIcon col="min" /></th>
                            <th style={sortableTh} onClick={() => handleSort('max')}>Max (s) <SortIcon col="max" /></th>
                            <th>Stddev (s)</th>
                            <th style={sortableTh} onClick={() => handleSort('rows')}>Rows <SortIcon col="rows" /></th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedRows.map((row, i) => {
                            const query = row.query || '';
                            const isTruncated = query.length > 100;
                            return (
                                <tr key={i}>
                                    <td>
                                        <code
                                            className="small d-block"
                                            style={{
                                                maxWidth: 400,
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-all',
                                                fontSize: '0.7rem',
                                            }}
                                        >
                                            {isTruncated ? query.slice(0, 100) + '…' : query}
                                        </code>
                                        {isTruncated && (
                                            <Button
                                                variant="link"
                                                size="sm"
                                                className="p-0 small"
                                                onClick={() => setModalQuery(query)}
                                            >
                                                View more
                                            </Button>
                                        )}
                                    </td>
                                    <td>{row.calls != null ? Number(row.calls).toLocaleString() : '—'}</td>
                                    <td>{row.avg_time_s ?? '—'}</td>
                                    <td>{row.total_time_s ?? '—'}</td>
                                    <td>{row.min_time_s ?? '—'}</td>
                                    <td>{row.max_time_s ?? '—'}</td>
                                    <td>{row.stddev_time_s ?? '—'}</td>
                                    <td>{row.rows != null ? Number(row.rows).toLocaleString() : '—'}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            </div>

            <Modal
                show={modalQuery !== null}
                onHide={() => { setModalQuery(null); setCopied(false); }}
                scrollable
                centered
                dialogClassName="modal-65w"
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fs-6">Full Query</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontSize: '0.8rem', margin: 0 }}>
                        {modalQuery}
                    </pre>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant={copied ? 'success' : 'outline-secondary'}
                        size="sm"
                        onClick={handleCopy}
                    >
                        <i className={`bi bi-${copied ? 'check-lg' : 'clipboard'} me-1`}></i>
                        {copied ? 'Copied!' : 'Copy'}
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => { setModalQuery(null); setCopied(false); }}>Close</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

const SLOW_QUERY_TABS = [
    { key: 'table_queries', label: 'Table Queries', icon: 'bi-table', field: 'slow_table_queries', emptyMessage: 'No slow table queries found.' },
    { key: 'function_calls', label: 'Function Calls', icon: 'bi-braces', field: 'slow_function_calls', emptyMessage: 'No slow function calls found.' },
    { key: 'insert_statements', label: 'Insert Statements', icon: 'bi-plus-square', field: 'slow_insert_statements', emptyMessage: 'No slow insert statements found.' },
    { key: 'update_statements', label: 'Update Statements', icon: 'bi-pencil-square', field: 'slow_update_statements', emptyMessage: 'No slow update statements found.' },
    { key: 'delete_statements', label: 'Delete Statements', icon: 'bi-dash-square', field: 'slow_delete_statements', emptyMessage: 'No slow delete statements found.' },
];

const SlowQueriesPanel = ({ data }) => {
    const [subTab, setSubTab] = useState('table_queries');

    return (
        <Tab.Container activeKey={subTab} onSelect={setSubTab}>
            <Nav variant="pills" className="mb-3" size="sm">
                {SLOW_QUERY_TABS.map(tab => (
                    <Nav.Item key={tab.key}>
                        <Nav.Link eventKey={tab.key} className="py-1 px-3 small">
                            <i className={`bi ${tab.icon} me-1`}></i>{tab.label}
                            {data?.[tab.field] && (
                                <Badge bg="secondary" className="ms-2">{data[tab.field].length}</Badge>
                            )}
                        </Nav.Link>
                    </Nav.Item>
                ))}
            </Nav>
            <Tab.Content>
                {SLOW_QUERY_TABS.map(tab => (
                    <Tab.Pane eventKey={tab.key} key={tab.key}>
                        <SlowQueryTable
                            rows={data?.[tab.field]}
                            emptyMessage={tab.emptyMessage}
                        />
                    </Tab.Pane>
                ))}
            </Tab.Content>
        </Tab.Container>
    );
};

// ── Main Database Panel ───────────────────────────────────────────────────────

const InfrastructureDatabasePanel = () => {
    const [activeTab, setActiveTab] = useState('replication');

    const [replication, setReplication] = useState(null);
    const [replicationLoading, setReplicationLoading] = useState(false);
    const [replicationError, setReplicationError] = useState('');

    const [vacuum, setVacuum] = useState(null);
    const [vacuumLoading, setVacuumLoading] = useState(false);
    const [vacuumError, setVacuumError] = useState('');

    const [slowQueries, setSlowQueries] = useState(null);
    const [slowQueriesLoading, setSlowQueriesLoading] = useState(false);
    const [slowQueriesError, setSlowQueriesError] = useState('');

    const fetchReplication = useCallback(async () => {
        setReplicationLoading(true);
        setReplicationError('');
        const res = await apiService.getDatabaseReplicationStatus();
        if (res.success) setReplication(res.data);
        else setReplicationError(res.error || 'Failed to load replication status');
        setReplicationLoading(false);
    }, []);

    const fetchVacuum = useCallback(async () => {
        setVacuumLoading(true);
        setVacuumError('');
        const res = await apiService.getDatabaseVacuumRequired();
        if (res.success) setVacuum(res.data);
        else setVacuumError(res.error || 'Failed to load vacuum data');
        setVacuumLoading(false);
    }, []);

    const fetchSlowQueries = useCallback(async () => {
        setSlowQueriesLoading(true);
        setSlowQueriesError('');
        const res = await apiService.getDatabaseSlowQueries();
        if (res.success) setSlowQueries(res.data);
        else setSlowQueriesError(res.error || 'Failed to load slow queries');
        setSlowQueriesLoading(false);
    }, []);

    const refreshMap = {
        replication: fetchReplication,
        vacuum: fetchVacuum,
        slow_queries: fetchSlowQueries,
    };

    const loadingMap = {
        replication: replicationLoading,
        vacuum: vacuumLoading,
        slow_queries: slowQueriesLoading,
    };

    const handleTabSelect = (key) => {
        setActiveTab(key);
        if (key === 'replication' && !replication && !replicationLoading) fetchReplication();
        if (key === 'vacuum' && !vacuum && !vacuumLoading) fetchVacuum();
        if (key === 'slow_queries' && !slowQueries && !slowQueriesLoading) fetchSlowQueries();
    };

    // Auto-fetch the default tab on first render
    React.useEffect(() => {
        fetchReplication();
    }, [fetchReplication]);

    const isCurrentLoading = loadingMap[activeTab];

    return (
        <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0"><i className="bi bi-database me-2"></i>Database</h5>
                <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={refreshMap[activeTab]}
                    disabled={isCurrentLoading}
                >
                    <i className="bi bi-arrow-clockwise me-1"></i>Refresh
                </Button>
            </Card.Header>
            <Card.Body className="p-0">
                <Tab.Container activeKey={activeTab} onSelect={handleTabSelect}>
                    <Nav variant="tabs" className="px-3 pt-2 border-bottom">
                        <Nav.Item>
                            <Nav.Link eventKey="replication">
                                <i className="bi bi-arrow-left-right me-1"></i>Replication
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="vacuum">
                                <i className="bi bi-trash3 me-1"></i>Vacuum Required
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="slow_queries">
                                <i className="bi bi-hourglass-split me-1"></i>Slow Queries
                            </Nav.Link>
                        </Nav.Item>
                    </Nav>
                    <Tab.Content className="p-3">
                        <Tab.Pane eventKey="replication">
                            <InfrastructureSectionPanel
                                loading={replicationLoading}
                                error={replicationError}
                                onRefresh={fetchReplication}
                            >
                                {replication && <ReplicationStatusPanel data={replication} />}
                            </InfrastructureSectionPanel>
                        </Tab.Pane>
                        <Tab.Pane eventKey="vacuum">
                            <InfrastructureSectionPanel
                                loading={vacuumLoading}
                                error={vacuumError}
                                onRefresh={fetchVacuum}
                            >
                                {vacuum && <VacuumRequiredPanel data={vacuum} />}
                            </InfrastructureSectionPanel>
                        </Tab.Pane>
                        <Tab.Pane eventKey="slow_queries">
                            <InfrastructureSectionPanel
                                loading={slowQueriesLoading}
                                error={slowQueriesError}
                                onRefresh={fetchSlowQueries}
                            >
                                {slowQueries && <SlowQueriesPanel data={slowQueries} />}
                            </InfrastructureSectionPanel>
                        </Tab.Pane>
                    </Tab.Content>
                </Tab.Container>
            </Card.Body>
        </Card>
    );
};

export default InfrastructureDatabasePanel;
